import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Users, Trash2, Edit2, Check, X } from 'lucide-react'
import { SharedProps } from '@/types'

type Member = {
  id: string
  name: string
  email_address: string
  role: 'member' | 'admin' | 'owner'
  removed: boolean
  created_at: string
}

type Account = {
  id: string
  name: string
  slug: string
  type: 'IndividualAccount' | 'TeamAccount'
  deleted: boolean
  members_count: number
  individual: boolean
  team: boolean
  created_at: string
}

type Props = {
  account: Account
  members: Member[]
}

function RoleBadge({ role }: { role: string }) {
  const config: Record<string, string> = {
    owner: 'bg-primary/10 text-primary border-primary/20',
    admin: 'bg-accent text-accent-foreground border-accent',
    member: 'bg-secondary text-secondary-foreground',
  }
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium border ${config[role] ?? config.member}`}>
      {role}
    </span>
  )
}

function InlineEditField({
  label,
  value,
  accountId,
  field,
}: {
  label: string
  value: string
  accountId: string
  field: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  const save = () => {
    router.patch(`/backoffice/accounts/${accountId}`, { account: { [field]: draft } }, {
      preserveScroll: true,
      onSuccess: () => setEditing(false),
    })
  }

  if (!editing) {
    return (
      <div className="flex items-center justify-between py-3">
        <div>
          <div className="text-xs text-muted-foreground mb-0.5">{label}</div>
          <div className="text-sm font-medium">{value}</div>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setDraft(value); setEditing(true) }}>
          <Edit2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 py-3">
      <div className="flex-1">
        <div className="text-xs text-muted-foreground mb-0.5">{label}</div>
        <Input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          className="h-7 text-sm"
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
        />
      </div>
      <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" onClick={save}>
        <Check className="w-3.5 h-3.5" />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => setEditing(false)}>
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  )
}

const ROLES = ['member', 'admin', 'owner'] as const

export default function BackofficeAccountsShow({ account, members }: Props) {
  const { auth } = usePage<SharedProps>().props
  const isAdmin = auth.identity?.backoffice_admin ?? false
  const confirm = useConfirm()

  const handleRoleChange = (memberId: string, newRole: string) => {
    router.patch(
      `/backoffice/accounts/${account.id}/members/${memberId}`,
      { role: newRole },
      { preserveScroll: true }
    )
  }

  const handleRemoveMember = async (member: Member) => {
    if (await confirm({
      description: `Remove ${member.name} from this workspace? They will lose access.`,
      confirmLabel: 'Remove',
      destructive: true,
    })) {
      router.delete(`/backoffice/accounts/${account.id}/members/${member.id}`, { preserveScroll: true })
    }
  }

  const handleDeleteWorkspace = async () => {
    if (await confirm({
      description: `Delete the workspace "${account.name}"? This is a soft delete — all data is preserved in the database.`,
      confirmLabel: 'Delete Workspace',
      destructive: true,
    })) {
      router.delete(`/backoffice/accounts/${account.id}`)
    }
  }

  const activeMembers = members.filter(m => !m.removed)
  const removedMembers = members.filter(m => m.removed)

  return (
    <BackofficeLayout
      title={account.name}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Workspaces', href: '/backoffice/accounts' },
        { label: account.name },
      ]}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-semibold tracking-tight">{account.name}</h1>
              <Badge variant="outline" className="text-[10px] font-normal">
                {account.individual ? 'Personal' : 'Team'}
              </Badge>
              {account.deleted && (
                <Badge className="text-[10px] bg-destructive/10 text-destructive border-destructive/20 font-normal">Deleted</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground font-mono">/{account.slug}</p>
          </div>
        </div>

        {/* Workspace Details — Editable */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium">Workspace Details</h2>
          </div>
          <div className="px-4 divide-y">
            {account.deleted || !isAdmin ? (
              <>
                <div className="py-3">
                  <div className="text-xs text-muted-foreground mb-0.5">Name</div>
                  <div className="text-sm font-medium">{account.name}</div>
                </div>
                <div className="py-3">
                  <div className="text-xs text-muted-foreground mb-0.5">Slug</div>
                  <div className="text-sm font-medium">{account.slug}</div>
                </div>
              </>
            ) : (
              <>
                <InlineEditField label="Name" value={account.name} accountId={account.id} field="name" />
                <InlineEditField label="Slug" value={account.slug} accountId={account.id} field="slug" />
              </>
            )}
            <div className="py-3">
              <div className="text-xs text-muted-foreground mb-0.5">Type</div>
              <div className="text-sm font-medium">{account.individual ? 'Personal Account' : 'Team Account'}</div>
            </div>
            <div className="py-3">
              <div className="text-xs text-muted-foreground mb-0.5">Created</div>
              <div className="text-sm font-medium">
                {new Date(account.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          </div>
        </section>

        {/* Members */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              Members ({activeMembers.length})
            </h2>
          </div>
          {activeMembers.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">No active members</div>
          ) : (
            <div className="divide-y">
              {activeMembers.map(member => (
                <div key={member.id} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{member.name}</span>
                      <RoleBadge role={member.role} />
                    </div>
                    <div className="text-xs text-muted-foreground">{member.email_address}</div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2 ml-4 shrink-0">
                      <select
                        value={member.role}
                        onChange={e => handleRoleChange(member.id, e.target.value)}
                        className="h-7 rounded border border-input bg-transparent px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      >
                        {ROLES.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemoveMember(member)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Removed Members (if any) */}
        {removedMembers.length > 0 && (
          <section className="border rounded-lg opacity-60">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-muted-foreground">Removed Members ({removedMembers.length})</h2>
            </div>
            <div className="divide-y">
              {removedMembers.map(member => (
                <div key={member.id} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate line-through text-muted-foreground">{member.name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">{member.email_address}</div>
                  </div>
                  <Badge variant="outline" className="text-[10px]">Removed</Badge>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Danger Zone */}
        {!account.deleted && isAdmin && (
          <section className="border border-destructive/20 rounded-lg">
            <div className="px-4 py-3 border-b border-destructive/20">
              <h2 className="text-sm font-medium text-destructive">Danger Zone</h2>
            </div>
            <div className="px-4 py-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Delete this workspace</div>
                <div className="text-xs text-muted-foreground">
                  Soft-delete — data is preserved in the database. This cannot be undone from the UI.
                </div>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteWorkspace}
                className="shrink-0 ml-4"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Delete Workspace
              </Button>
            </div>
          </section>
        )}
      </div>
    </BackofficeLayout>
  )
}
